import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import DrawerStackContext from './DrawerStackContext';
import DrawerStack from './DrawerStack';

export default function DrawerStackProvider({ children, sharedDrawers = [] }) {
    const [drawers, setDrawers] = useState([]);
    const [drawerTypes, setDrawerTypes] = useState(() => {
        return sharedDrawers.reduce((acc, [id, component]) => {
            acc[id] = component;
            return acc;
        }, {});
    });
    const [currentModalIndex, setCurrentModalIndex] = useState(0);
    const [direction, setDirection] = useState(1);

    const register = useCallback((paramsOrId, component) => {
        function registerSingleDrawer(id, Component) {
            setDrawerTypes((prev) => ({ ...prev, [id]: Component }));

            return () => {
                setDrawerTypes((prev) => {
                    const { [id]: _, ...rest } = prev;
                    return rest;
                });
            };
        }

        function registerMultipleDrawers(drawerObject) {
            setDrawerTypes((prev) => ({ ...prev, ...drawerObject }));

            return () => {
                setDrawerTypes((prev) => {
                    const newTypes = { ...prev };
                    Object.keys(drawerObject).forEach((id) => {
                        delete newTypes[id];
                    });
                    return newTypes;
                });
            };
        }

        if (Array.isArray(paramsOrId)) {
            const drawerObject = paramsOrId.reduce((acc, [id, component]) => {
                acc[id] = component;
                return acc;
            }, {});
            return registerMultipleDrawers(drawerObject);
        } else {
            return registerSingleDrawer(paramsOrId, component);
        }
    }, []);

    const open = useCallback(
        (id, props = {}) => {
            if (!drawerTypes[id]) {
                console.warn(`Drawer type "${id}" is not registered.`);
                return;
            }
            const component = drawerTypes[id];

            setDirection(1);
            setCurrentModalIndex(drawers.length);
            setDrawers((prev) => [...prev, { id, props, component }]);
        },
        [drawerTypes, drawers.length],
    );

    const handleBackNavigation = useCallback(() => {
        setDirection(-1);
        if (currentModalIndex > 0) {
            setCurrentModalIndex((prev) => prev - 1);
            setDrawers((prev) => prev.slice(0, -1));
        }
    }, [currentModalIndex]);

    const handleClose = useCallback(() => {
        setDrawers([]);
    }, []);

    const contextValue = useMemo(
        () => ({
            register,
            open,
        }),
        [register, open],
    );

    return (
        <DrawerStackContext.Provider value={contextValue}>
            {children}
            <DrawerStack
                direction={direction}
                index={currentModalIndex}
                drawers={drawers}
                onBack={handleBackNavigation}
                onClose={handleClose}
            />
        </DrawerStackContext.Provider>
    );
}

DrawerStackProvider.propTypes = {
    children: PropTypes.node.isRequired,
    sharedDrawers: PropTypes.arrayOf(PropTypes.array),
};
